import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { useAuth } from "@/lib/auth";
import { 
  Plane, 
  FileText, 
  Share2, 
  Sparkles,
  ArrowRight,
  CheckCircle2
} from "lucide-react";

const features = [
  {
    icon: Plane,
    title: "Connect TripIt",
    description: "Sync your trips automatically from TripIt with a single click.",
  },
  {
    icon: FileText,
    title: "Professional PDFs",
    description: "Generate clean, Sabre-style itineraries with your custom branding.",
  },
  {
    icon: Share2,
    title: "Easy Sharing",
    description: "Download, email, or share a link to your formatted itinerary.",
  },
  {
    icon: Sparkles,
    title: "Full Trip Details",
    description: "Flights, hotels, car rentals, and activities - all in one document.",
  },
];

const benefits = [
  "Clean, professional formatting",
  "Custom logo branding",
  "Multiple sharing options",
  "Secure & private",
];

export default function Index() {
  const { user } = useAuth();

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative overflow-hidden gradient-hero py-24 md:py-32">
          {/* Decorative elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-accent/20 blur-3xl" />
            <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-primary-foreground/10 blur-3xl" />
          </div>
          
          <div className="container relative z-10">
            <div className="mx-auto max-w-3xl text-center">
              <h1 className="animate-slide-up font-display text-4xl font-bold tracking-tight text-primary-foreground md:text-6xl">
                Turn Your Trips Into
                <span className="block text-accent-foreground/90">Professional Itineraries</span>
              </h1>
              
              <p className="mt-6 animate-slide-up text-lg text-primary-foreground/80 md:text-xl" style={{ animationDelay: "0.1s" }}>
                Connect your TripIt account and instantly generate beautiful, 
                Sabre-style travel documents with your custom branding.
              </p>
              
              <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:justify-center" style={{ animationDelay: "0.2s" }}>
                <Link to={user ? "/trips" : "/auth?mode=signup"}>
                  <Button variant="hero" size="xl" className="w-full sm:w-auto">
                    {user ? "View My Trips" : "Get Started Free"}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                {!user && (
                  <Link to="/auth">
                    <Button variant="hero-outline" size="xl" className="w-full sm:w-auto">
                      Sign In
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 md:py-28">
          <div className="container">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="font-display text-3xl font-bold text-foreground md:text-4xl">
                Everything You Need
              </h2>
              <p className="mt-4 text-muted-foreground">
                From TripIt to professional PDF in just a few clicks
              </p>
            </div>
            
            <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {features.map((feature, index) => (
                <div 
                  key={feature.title}
                  className="group rounded-2xl border border-border bg-card p-6 shadow-soft transition-all duration-300 hover:shadow-elevated hover:-translate-y-1"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="mb-4 inline-flex rounded-xl bg-accent/10 p-3 text-accent transition-colors group-hover:bg-accent group-hover:text-accent-foreground">
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-card-foreground">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="bg-muted/50 py-20 md:py-28">
          <div className="container">
            <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
              <div>
                <h2 className="font-display text-3xl font-bold text-foreground md:text-4xl">
                  Professional Travel Documents,{" "}
                  <span className="text-accent">Personalized</span>
                </h2>
                <p className="mt-4 text-lg text-muted-foreground">
                  Upload your company logo and create branded itineraries that 
                  look like they came from a professional travel agency.
                </p>
                
                <ul className="mt-8 space-y-4">
                  {benefits.map((benefit) => (
                    <li key={benefit} className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-accent" />
                      <span className="text-foreground">{benefit}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="mt-8">
                  <Link to={user ? "/trips" : "/auth?mode=signup"}>
                    <Button variant="accent" size="lg">
                      {user ? "Create Itinerary" : "Start Creating"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
              
              <div className="relative">
                <div className="rounded-2xl border border-border bg-card p-6 shadow-elevated">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 border-b border-border pb-4">
                      <div className="h-12 w-24 rounded bg-accent/20 flex items-center justify-center text-accent font-bold text-sm">
                        Your Logo
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-card-foreground">Sun, 26 May 2024</p>
                        <p className="text-xs text-muted-foreground">TRIP TO Jakarta (CGK) - Vienna (VIE)</p>
                      </div>
                    </div>
                    
                    <div className="rounded-lg border border-border overflow-hidden">
                      <div className="bg-muted px-4 py-2 flex items-center gap-2">
                        <Plane className="h-4 w-4 text-accent" />
                        <span className="text-sm font-medium">Departure: Sunday 26 May</span>
                      </div>
                      <div className="grid grid-cols-4 text-xs">
                        <div className="border-r border-border p-3">
                          <p className="text-muted-foreground">Flight</p>
                          <p className="font-medium mt-1">EY 475</p>
                        </div>
                        <div className="border-r border-border p-3">
                          <p className="font-semibold">Jakarta</p>
                          <p className="text-muted-foreground text-[10px]">(CGK)</p>
                          <p className="mt-1">17:45</p>
                        </div>
                        <div className="border-r border-border p-3">
                          <p className="font-semibold">Abu Dhabi</p>
                          <p className="text-muted-foreground text-[10px]">(AUH)</p>
                          <p className="mt-1">22:40</p>
                        </div>
                        <div className="p-3">
                          <p className="text-muted-foreground">Aircraft</p>
                          <p className="font-medium mt-1">Boeing 777</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="absolute -bottom-4 -right-4 -z-10 h-full w-full rounded-2xl bg-accent/20" />
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 md:py-28">
          <div className="container">
            <div className="mx-auto max-w-3xl rounded-3xl gradient-hero p-8 text-center md:p-12">
              <h2 className="font-display text-2xl font-bold text-primary-foreground md:text-3xl">
                Ready to Transform Your Travel Documents?
              </h2>
              <p className="mt-4 text-primary-foreground/80">
                Join travelers who create professional itineraries in seconds.
              </p>
              <div className="mt-8">
                <Link to={user ? "/trips" : "/auth?mode=signup"}>
                  <Button variant="hero" size="lg">
                    {user ? "Go to Dashboard" : "Get Started Free"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
